import React, { useState, useEffect, useRef, useCallback } from 'react';
import { LevelData } from '../data/curriculum';
import { useTTS } from '../hooks/useTTS';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { matchesSpeech } from '../utils/speechMatcher';
import { playSuccessSound } from '../utils/sounds';
import CountdownTimer from '../components/CountdownTimer';
import MicrophoneIndicator from '../components/MicrophoneIndicator';
import { motion, AnimatePresence } from 'framer-motion';
import TTSPlaybackIndicator from '../components/TTSPlaybackIndicator';

interface Step2RepeatRuleProps {
  level: LevelData;
  onComplete: () => void;
}

type Phase = 'playing_tts' | 'countdown' | 'recording' | 'transcribing' | 'success' | 'retry' | 'max_retries' | 'error';

const MAX_RETRIES = 3;

const Step2RepeatRule: React.FC<Step2RepeatRuleProps> = ({ level, onComplete }) => {
  const [phase, setPhase] = useState<Phase>('playing_tts');
  const [retryCount, setRetryCount] = useState(0);
  const { playText, isLoading, isPlaying, error: ttsError, stop } = useTTS();
  const { startListening, isRecording, isTranscribing, micPermissionDenied, volume } = useSpeechRecognition();
  const hasStarted = useRef(false);
  const isMountedRef = useRef(true);

  // Track mount status
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Play TTS prompt on mount
  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    let cancelled = false;

    const play = async () => {
      await playText("Now it's your turn — say the rule out loud.");
      if (cancelled) return;
      setPhase('countdown');
    };
    play();

    return () => {
      cancelled = true;
      stop();
      hasStarted.current = false;
    };
  }, [playText, stop]);

  // Handle mic permission denied
  useEffect(() => {
    if (micPermissionDenied && phase === 'recording') {
      setPhase('error');
    }
  }, [micPermissionDenied, phase]);

  // Auto-advance on success or mic error
  useEffect(() => {
    if (phase === 'success') {
      const timer = setTimeout(onComplete, 1500);
      return () => clearTimeout(timer);
    }
    if (phase === 'error') {
      const timer = setTimeout(onComplete, 2000);
      return () => clearTimeout(timer);
    }
  }, [phase, onComplete]);

  // Auto-advance from retry back to countdown
  useEffect(() => {
    if (phase === 'retry') {
      const timer = setTimeout(() => setPhase('countdown'), 1500);
      return () => clearTimeout(timer);
    }
  }, [phase]);

  const handleCountdownComplete = useCallback(async () => {
    setPhase('recording');

    const { transcript, micDenied } = await startListening();

    if (!isMountedRef.current) return;

    // startListening returns micDenied: true on mic error
    if (micDenied) {
      setPhase('error');
      return;
    }

    setPhase('transcribing');

    if (matchesSpeech(transcript, level.rule)) {
      playSuccessSound();
      setPhase('success');
    } else {
      const newRetryCount = retryCount + 1;
      setRetryCount(newRetryCount);

      if (newRetryCount >= MAX_RETRIES) {
        setPhase('max_retries');
      } else {
        setPhase('retry');
      }
    }
  }, [startListening, level.rule, retryCount]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-10 rounded-2xl shadow-lg text-center max-w-lg w-full"
    >
      <div className="text-sm font-semibold text-indigo-500 uppercase tracking-wide mb-3">
        Repeat the Rule
      </div>

      <p className="text-xl text-gray-700 leading-relaxed mb-6">{level.rule}</p>

      <AnimatePresence mode="wait">
        {phase === 'playing_tts' && (
          <motion.div key="tts" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <TTSPlaybackIndicator isLoading={isLoading} isPlaying={isPlaying} error={ttsError} />
          </motion.div>
        )}

        {phase === 'countdown' && (
          <motion.div key="countdown" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <p className="text-gray-500 text-sm mb-2">Get ready to speak!</p>
            <CountdownTimer onComplete={handleCountdownComplete} />
          </motion.div>
        )}

        {(phase === 'recording' || phase === 'transcribing') && (
          <motion.div key="recording" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <MicrophoneIndicator isRecording={isRecording} isTranscribing={isTranscribing} volume={volume} />
          </motion.div>
        )}

        {phase === 'success' && (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="text-green-500 font-bold text-lg"
          >
            Great job!
          </motion.div>
        )}

        {phase === 'retry' && (
          <motion.div
            key="retry"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="text-amber-500 font-bold text-lg"
          >
            Let's try again!
          </motion.div>
        )}

        {phase === 'max_retries' && (
          <motion.div
            key="max_retries"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center gap-3"
          >
            <p className="text-indigo-500 font-medium text-lg">Let's try again when you're ready!</p>
            <button
              onClick={() => { setRetryCount(0); setPhase('countdown'); }}
              className="bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
            >
              Try Again
            </button>
          </motion.div>
        )}

        {phase === 'error' && (
          <motion.div
            key="error"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="text-indigo-500 font-medium text-lg"
          >
            Let's move on.
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Step2RepeatRule;
